import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { User, ShieldCheck, Eye, EyeOff } from 'lucide-react';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginType, setLoginType] = useState('staff'); // 'staff' or 'patient'
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const loadingToast = toast.loading('Authenticating...');
    try {
      const endpoint = loginType === 'staff' 
        ? `${window.location.protocol}//${window.location.hostname}/api/auth/login` 
        : `${window.location.protocol}//${window.location.hostname}/api/auth/patient-login`;
      
      const response = await axios.post(endpoint, { email, password });
      
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('userRole', loginType === 'staff' ? response.data.user.role : 'patient');
      localStorage.setItem('userName', loginType === 'staff' ? response.data.user.username : response.data.patient.firstName);
      if (loginType === 'patient') {
        localStorage.setItem('patientId', response.data.patient.id);
      }
      
      toast.success('Welcome back!', { id: loadingToast });
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Login failed. Please check your credentials.', { id: loadingToast });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0F172A] p-4 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full opacity-20">
          <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-indigo-500 blur-[120px]" />
          <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-blue-600 blur-[120px]" />
      </div>

      <div className="w-full max-w-md bg-white border border-slate-200 rounded-[40px] shadow-2xl relative z-10 overflow-hidden">
        <div className="flex border-b border-slate-100">
            <button 
                onClick={() => setLoginType('staff')}
                className={`flex-1 py-5 text-[11px] font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all ${
                    loginType === 'staff' ? 'text-indigo-600 bg-white' : 'text-slate-400 bg-slate-50/50'
                }`}
            >
                <ShieldCheck className={`w-4 h-4 ${loginType === 'staff' ? 'animate-pulse' : ''}`} />
                Hospital Staff
                {loginType === 'staff' && <div className="absolute bottom-0 w-1/2 h-[3px] bg-indigo-600 rounded-full" />}
            </button>
            <button 
                onClick={() => setLoginType('patient')}
                className={`flex-1 py-5 text-[11px] font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all relative ${
                    loginType === 'patient' ? 'text-blue-600 bg-white' : 'text-slate-400 bg-slate-50/50'
                }`}
            >
                <User className={`w-4 h-4 ${loginType === 'patient' ? 'symbol-pulse' : ''}`} />
                Patient Portal
                {loginType === 'patient' && <div className="absolute bottom-0 w-1/2 h-[3px] bg-blue-600 rounded-full" />}
            </button>
        </div>

        <div className="p-10">
            <div className="mb-10 text-center">
                <h2 className="text-3xl font-black text-slate-800 tracking-tight">
                    {loginType === 'staff' ? 'Clinical Access' : 'Patient Access'}
                </h2>
                <p className="text-[12px] text-slate-400 font-bold uppercase tracking-widest mt-2">
                    {loginType === 'staff' ? 'Enterprise Medical Dashboard' : 'Your Personal Health Hub'}
                </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-4">Email Address</label>
                    <input
                        type="email"
                        placeholder="name@unumed.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="w-full h-14 rounded-2xl border border-slate-200 bg-slate-50 px-6 text-sm font-bold text-slate-700 outline-none transition-all focus:ring-4 focus:ring-indigo-500/5 focus:border-indigo-500 focus:bg-white"
                    />
                </div>

                <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-4">Access Password</label>
                    <div className="relative">
                        <input
                            type={showPassword ? 'text' : 'password'}
                            placeholder="••••••••"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            className="w-full h-14 rounded-2xl border border-slate-200 bg-slate-50 px-6 pr-14 text-sm font-bold text-slate-700 outline-none transition-all focus:ring-4 focus:ring-indigo-500/5 focus:border-indigo-500 focus:bg-white"
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-4 top-1/2 -translate-y-1/2 p-2 hover:bg-slate-200 rounded-xl transition-all text-slate-400"
                        >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                    </div>
                </div>

                <button
                    type="submit"
                    className={`w-full py-5 rounded-[24px] font-black text-[12px] uppercase tracking-[0.2em] shadow-2xl transition-all active:scale-95 text-white ${
                        loginType === 'staff' 
                        ? 'bg-indigo-600 hover:bg-black shadow-indigo-200' 
                        : 'bg-blue-600 hover:bg-black shadow-blue-200'
                    }`}
                >
                    Secure Login
                </button>
            </form>

            <div className="mt-10 pt-8 border-t border-slate-100 flex items-center justify-between">
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Unumed Integrated v4.0</p>
                <button className="text-[11px] font-black text-indigo-600 uppercase tracking-widest hover:underline">Forgot Access?</button>
            </div>
        </div>
      </div>
    </div>
  );
};

export default Login;